import type { PlanFlight, PlanMatrixGroup } from "@/lib/types";

export type SortKey = "date" | "duration" | "price" | "stops" | "departure";

export interface FlightFilters {
  airlines: string[]; // IATA codes; empty = all
  /**
   * § G30 — departure AIRPORTS to keep; empty = all.
   *
   * Separate from `airlines` because they answer different questions and a
   * traveller shopping several airports asks the airport one first: "which of
   * these leave from LaGuardia?" is a commute decision, and for somebody in
   * Manhattan an hour to EWR can outweigh $40 of fare.
   */
  origins: string[];
  maxStops: number | null; // null = any
  maxPrice: number | null;
  maxDurationMinutes: number | null;
}

export interface FlightViewState {
  sort: SortKey;
  filters: FlightFilters;
}

export interface FlightRow {
  flight: PlanFlight;
  date: string;
  dateLabel: string;
  /** § G30 — the airport this option really departs. Null when unknown. */
  origin?: string | null;
}

export const DEFAULT_VIEW_STATE: FlightViewState = {
  sort: "date",
  filters: { airlines: [], origins: [], maxStops: null, maxPrice: null, maxDurationMinutes: null },
};

export function distinctAirlines(groups: PlanMatrixGroup[]): string[] {
  const set = new Set<string>();
  for (const g of groups) {
    for (const fl of g.flights) {
      const code = (fl.airline || "").trim().toUpperCase();
      if (code) set.add(code);
    }
  }
  return [...set].sort();
}

/**
 * Which airport this option actually LEAVES FROM.
 *
 * ⚠️ Read from the first SEGMENT, not from the leg it was filed under. A leg
 * whose origin is a metro code ("NYC") holds JFK, LGA and EWR departures all
 * mixed together — which is exactly the state Wade described: "3 sets of
 * flights ... still I can't tell which airport it is". The segment knows, and
 * it is the only thing that does.
 *
 * Falls back to the leg's own origin when no segment detail was captured, and
 * to null when neither is known — a filter must never silently drop an option
 * into a bucket we are guessing at.
 */
export function originOf(
  fl: PlanFlight,
  legOrigin?: string | null,
  role?: 'outbound' | 'return' | 'extra' | null,
): string | null {
  const ordered = (fl.segments ?? []).slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  // ⚠️ On a RETURN leg the traveller's own airport is the ARRIVAL, not the
  // departure. Every option on Stephen's way home leaves ATL, so keying on the
  // departure gave one chip and told him nothing — while the fact he actually
  // needs ("does this land at LGA or EWR?") sat in the last segment. Wade asked
  // for the airports "to/from NYC"; this is the "to" half.
  const isReturn = role === 'return';
  const seg = isReturn ? ordered[ordered.length - 1] : ordered[0];
  const fromSegment = ((isReturn ? seg?.destination : seg?.origin) || '').trim().toUpperCase();
  if (fromSegment) return fromSegment;

  const fromLeg = (legOrigin || '').trim().toUpperCase();
  return fromLeg || null;
}

/**
 * Every airport actually in play, sorted. Drives the filter chips — and by
 * being derived from the flights rather than the routes, it tells the truth
 * even when several airports were shopped under one metro leg.
 */
export function distinctOrigins(
  groups: PlanMatrixGroup[],
  legOrigin?: string | null,
  role?: 'outbound' | 'return' | 'extra' | null,
): string[] {
  const set = new Set<string>();
  for (const g of groups) {
    for (const fl of g.flights) {
      const code = originOf(fl, legOrigin, role);
      if (code) set.add(code);
    }
  }
  return [...set].sort();
}

export function passesFilters(
  fl: PlanFlight,
  filters: FlightFilters,
  legOrigin?: string | null,
  role?: 'outbound' | 'return' | 'extra' | null,
): boolean {
  if (filters.origins?.length) {
    const code = originOf(fl, legOrigin, role);
    // An option whose airport we cannot establish is NOT quietly hidden by an
    // airport filter — hiding it would look like the flight ceased to exist.
    if (code && !filters.origins.includes(code)) return false;
  }
  if (filters.airlines.length) {
    const code = (fl.airline || "").trim().toUpperCase();
    if (!filters.airlines.includes(code)) return false;
  }
  if (filters.maxStops != null && (fl.stops ?? 99) > filters.maxStops) return false;
  if (filters.maxPrice != null && (fl.price ?? Infinity) > filters.maxPrice) return false;
  if (filters.maxDurationMinutes != null && (fl.durationMinutes ?? Infinity) > filters.maxDurationMinutes) return false;
  return true;
}

// Nulls always sort last for numeric ascending keys.
function numAsc(a: number | null | undefined, b: number | null | undefined): number {
  const av = a == null ? Infinity : a;
  const bv = b == null ? Infinity : b;
  return av - bv;
}

function depMinutes(fl: PlanFlight): number {
  const m = (fl.departLocal || "").trim().match(/^(\d{1,2}):(\d{2})/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : Infinity;
}

export function buildFlightRows(
  groups: PlanMatrixGroup[],
  state: FlightViewState,
  legOrigin?: string | null,
  role?: 'outbound' | 'return' | 'extra' | null,
): { rows: FlightRow[]; total: number; shown: number } {
  const all: FlightRow[] = [];
  for (const g of groups) {
    for (const fl of g.flights) {
      all.push({
        flight: fl,
        date: g.date,
        dateLabel: g.dateLabel || g.date,
        // § G30 — carried on the row so every option can SAY which airport it
        // leaves from. "I can't tell which airport it is" was the complaint.
        origin: originOf(fl, legOrigin, role),
      });
    }
  }
  const total = all.length;
  const filtered = all.filter((r) => passesFilters(r.flight, state.filters, legOrigin, role));

  const byDateThenDep = (a: FlightRow, b: FlightRow) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : depMinutes(a.flight) - depMinutes(b.flight);

  let sorted: FlightRow[];
  switch (state.sort) {
    case "duration":
      sorted = [...filtered].sort((a, b) => numAsc(a.flight.durationMinutes, b.flight.durationMinutes) || byDateThenDep(a, b));
      break;
    case "price":
      sorted = [...filtered].sort((a, b) => numAsc(a.flight.price, b.flight.price) || byDateThenDep(a, b));
      break;
    case "stops":
      sorted = [...filtered].sort((a, b) => numAsc(a.flight.stops, b.flight.stops) || byDateThenDep(a, b));
      break;
    case "departure":
      sorted = [...filtered].sort((a, b) => depMinutes(a.flight) - depMinutes(b.flight) || byDateThenDep(a, b));
      break;
    case "date":
    default:
      sorted = [...filtered].sort(byDateThenDep);
      break;
  }
  return { rows: sorted, total, shown: sorted.length };
}

export interface FlightGroup {
  key: string;
  headline: FlightRow;
  variants: FlightRow[];
  count: number;
}

/** Group already-sorted+filtered rows by the OBSERVED first flight: same carrier +
 *  same exact departure time on the same date. An airline doesn't run two of its
 *  own flights out of the same airport in the same minute, so these share a first
 *  flight; the variants differ by connection (hub / 2nd leg / arrival / price).
 *  This is real, observed schedule data straight from the results — no flight-number
 *  lookup or external API needed. Because `rows` arrive in active-sort order, the
 *  FIRST row per key is the best (headline); the rest become variants.
 *  (Edge case: an airline with two same-minute departures from one airport to
 *  different hubs would over-group; rare, and both are genuine same-time options.) */
export function buildFlightGroups(rows: FlightRow[]): FlightGroup[] {
  const groups: FlightGroup[] = [];
  const index = new Map<string, number>();
  for (const r of rows) {
    const carrier = (r.flight.airline || "").toUpperCase();
    const dep = (r.flight.departLocal || "").trim();
    if (carrier && dep) {
      const key = `${carrier}|${dep}|${r.date}`;
      const at = index.get(key);
      if (at == null) {
        index.set(key, groups.length);
        groups.push({ key, headline: r, variants: [], count: 1 });
      } else {
        groups[at].variants.push(r);
        groups[at].count++;
      }
    } else {
      groups.push({ key: `solo:${r.flight.flightId}`, headline: r, variants: [], count: 1 });
    }
  }
  return groups;
}
